import { useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Field } from "formik";
import { ArrowDown, ArrowUp, Snowflake, Zap } from "lucide-react";
import errorToast from "../../../helper-components/error-toast";
import { ChevronDown } from "lucide-react";
import LoadingOverlay from "../../../helper-components/overlay-loading";
const Label = ({ children, required }) => (
  <label className="text-[12px] font-semibold text-[#2B2F38]">
    {children} {required ? <span className="text-[#EF4444]">*</span> : null}
  </label>
);

const InputField = ({ name, placeholder = "Placeholder", required = true, type = "text" }) => (
  <div className="flex flex-col gap-2">
    <Label required={required}>{placeholder === "Placeholder" ? name : name}</Label>
    <Field
      name={name}
      type={type}
      placeholder="Placeholder"
      className="w-full h-[40px] rounded-[8px] border border-[#E6EAF2] bg-white px-3 text-[13px] text-[#2B2F38] placeholder:text-[#A0A7B4] focus:outline-none focus:ring-0"
    />
  </div>
);

const SelectField = ({ name, label, required = true }) => (
  <div className="flex flex-col gap-2">
    <Label required={required}>{label}</Label>

    <div className="relative">
      <Field
        as="select"
        name={name}
        className="w-full h-[40px] appearance-none rounded-[8px] border border-[#E6EAF2] bg-white px-3 pr-9 text-[13px] text-[#A0A7B4] focus:outline-none focus:ring-0"
        defaultValue=""
      >
        <option value="" disabled>
          Placeholder
        </option>
        {/* add real options later */}
        <option value="opt1">Option 1</option>
        <option value="opt2">Option 2</option>
      </Field>

      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280]">
        <ChevronDown size={16} />
      </div>
    </div>
  </div>
);
const VMProductSidePanel = ({ open, setOpen }) => {
  const [loading, setLoading] = useState(false);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => setOpen(false)}
      >
        <div className="fixed inset-0 overflow-hidden">
          {/* Background Overlay */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          {/* Slide-in Panel */}
          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full relative w-[400px] sm:w-[600px] bg-white rounded-tl-lg rounded-bl-lg shadow-xl flex flex-col">
                
                {/* Header */}
                <div className="p-4 flex justify-between items-center border-b border-[var(--button-border-color)]">
                  <div>
                    <h2 className="text-lg font-semibold">Create New Product</h2>
                    {/* <div className="text-sm pt-1 text-[var(--description-color)]">
                      Min & Max Calculations
                    </div> */}
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>

                <div className="px-5 pb-4 max-h-[calc(100vh-160px)] overflow-y-auto mt-4">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <Label required>Item Name</Label>
                    <Field
                      name="item_name"
                      placeholder="Placeholder"
                      className="w-full h-[40px] rounded-[8px] border border-[#E6EAF2] bg-white px-3 text-[13px] text-[#2B2F38] placeholder:text-[#A0A7B4] focus:outline-none focus:ring-0"
                    />
                  </div>

                  <SelectField name="item_type" label="Item Type" required />
                  <SelectField name="item_category" label="Item Category" required />
                  <SelectField name="item_group" label="Item Group" required />

                  <div className="flex flex-col gap-2">
                    <Label required>Barcode</Label>
                    <Field
                      name="barcode"
                      placeholder="Placeholder"
                      className="w-full h-[40px] rounded-[8px] border border-[#E6EAF2] bg-white px-3 text-[13px] text-[#2B2F38] placeholder:text-[#A0A7B4] focus:outline-none focus:ring-0"
                    />
                  </div>

                  <SelectField name="schedule" label="Schedule (SCH)" required />
                  <SelectField name="manufacture" label="Manufacture" required />
                  <SelectField name="uom" label="Unit of Measure(UOM)" required />

                  {/* Pack Size + Pack Quantity (two columns) */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <Label required>Pack Size</Label>
                      <Field
                        name="pack_size"
                        placeholder="Placeholder"
                        className="w-full h-[40px] rounded-[8px] border border-[#E6EAF2] bg-white px-3 text-[13px] text-[#2B2F38] placeholder:text-[#A0A7B4] focus:outline-none focus:ring-0"
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <Label required={false}>Pack Quantity</Label>
                      <Field
                        name="pack_qty"
                        placeholder="Placeholder"
                        className="w-full h-[40px] rounded-[8px] border border-[#E6EAF2] bg-white px-3 text-[13px] text-[#2B2F38] placeholder:text-[#A0A7B4] focus:outline-none focus:ring-0"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label required>HSN Code</Label>
                    <Field
                      name="hsn_code"
                      placeholder="Placeholder"
                      className="w-full h-[40px] rounded-[8px] border border-[#E6EAF2] bg-white px-3 text-[13px] text-[#2B2F38] placeholder:text-[#A0A7B4] focus:outline-none focus:ring-0"
                    />
                  </div>

                  <SelectField name="tax_category" label="Tax Category" required />
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-4 border-t border-[#E6EAF2] bg-white flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="h-[34px] px-4 rounded-[6px] bg-[#B91C1C] text-white text-[13px] font-semibold hover:opacity-95"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={() => {
                    // hook save later
                    setOpen(false);
                  }}
                  className="h-[34px] px-4 rounded-[6px] bg-primary text-white text-[13px] font-semibold hover:opacity-95"
                >
                  Save Items
                </button>
              </div>

                {/* Loading Overlay */}
                <LoadingOverlay isLoading={loading} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default VMProductSidePanel;
